import { useContextMenuMESF } from "@dexteel/mesf-core";
import { Grid, Typography } from "@mui/material";
import {
  DefaultMenuItem,
  GetContextMenuItemsParams,
  GridApi,
  IRowNode,
  MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CenteredLazyLoading } from "../../../../../controls/LazyLoading";
import { useGridDefinitions } from "../../../../../controls/ag-grid/components/GridDefinitions";
import { FooterButton } from "../../../../../controls/buttons/FooterButton";
import { TextFieldSearchFilter } from "../../../../../shared/TextFieldSearchFilter";
import { CatalogBin } from "../../../models/CatalogBin";
import { useBinsOptionsFunctions } from "../hooks/useBinsOptionsFunctions";
import { useTableData } from "../hooks/useTableData";
import { BinModal } from "./BinModal";

type onModalHideProps = {
  entityId?: number;
  shouldUpdate?: boolean;
};

type Props = {
  getCatalogBinsFromAPI: Function;
  isLoading: boolean;
  binId: String | undefined;
};

export const TableBin = ({
  getCatalogBinsFromAPI,
  isLoading,
  binId,
}: Props) => {
  const navigate = useNavigate();

  const [filterValue, setFilterValue] = useState("");
  const [showModal, setShowModal] = useState<"new" | "edit" | "delete" | "">(
    "",
  );
  const [bin, setBin] = useState<CatalogBin | null>(null);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  const { showContextMenu, registerConfig } = useContextMenuMESF();

  const handleNewBin = () => {
    if (gridApi) gridApi.deselectAll();
    setShowModal("new");
    setBin(null);
    navigate(`/configuration/steel-bins/new`);
  };

  const { getMenuOptions } = useBinsOptionsFunctions({
    setBin: setBin,
    setShowModal,
    handleNewBin,
  });
  const { rows, columnDefs } = useTableData({
    setBin,
    setShowModal,
    showContextMenu,
  });
  const { columnSimpleDefaults } = useGridDefinitions({
    OnEdit: (data: CatalogBin) => {
      setShowModal("edit");
      setBin(data);
    },
  });

  const onRowDoubleClicked = (event: any) => {
    const cell = gridApi?.getFocusedCell();
    if (cell?.column.getColId() !== "-" && event.node.isSelected()) {
      setBin(event.data as CatalogBin);
      setShowModal("edit");
      navigate(`/configuration/steel-bins/${(event.data as CatalogBin).BinId}`);
    }
  };

  const onCellClicked = (event: any) => {
    if (event.column.colId === "-")
      navigate(`/configuration/steel-bins/${(event.data as CatalogBin).BinId}`);
  };

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
      const data = params.node?.data;
      const nativeEvent = (
        params as GetContextMenuItemsParams & { event?: MouseEvent }
      ).event;

      if (data && nativeEvent) {
        showContextMenu(
          nativeEvent as unknown as React.MouseEvent,
          data,
          "binContext",
        );
        params.api.deselectAll();
        params.node?.setSelected(true);
      }
      return [];
    },
    [rows],
  );

  const onModalHide = ({ entityId, shouldUpdate }: onModalHideProps) => {
    if (entityId && shouldUpdate) {
      setTimeout(() => {
        const rowNode = gridApi?.getRowNode(String(entityId));
        if (rowNode) {
          gridApi?.ensureIndexVisible(rowNode.rowIndex ?? 0, "middle");
          rowNode?.setSelected(true);
        }
      }, 0);
    }
    setShowModal("");
    setBin(null);
    navigate(`/configuration/steel-bins/`);
  };

  useEffect(() => {
    if (binId) {
      if (binId === "new") {
        handleNewBin();
      } else {
        let binNode: IRowNode<any>;

        gridApi?.forEachNode((n) => {
          if ((n.data as CatalogBin).BinId === Number(binId)) {
            binNode = n;
            return true;
          }
        });

        if (binNode!) {
          gridApi?.ensureIndexVisible(binNode.rowIndex ?? 0, "middle");
          binNode.setSelected(true);
          setBin(binNode.data);
          setShowModal("edit");
        }
      }
    }
  }, [gridApi]);

  useEffect(() => {
    registerConfig({
      id: "binContext",
      getOptions: getMenuOptions,
    });
  }, []);

  return (
    <>
      <Grid container justifyContent="center">
        <Grid item md={12} xs={12} style={{ padding: "0 15px" }}>
          <Typography variant="h5" style={{ fontWeight: 600 }}>
            Bins
          </Typography>
        </Grid>
        <Grid item md={12} xs={12} style={{ margin: "10px 18px" }}>
          <TextFieldSearchFilter
            value={filterValue}
            onChange={(val) => {
              setFilterValue(val);
              gridApi?.setGridOption("quickFilterText", val);
            }}
          />
        </Grid>
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          style={{ width: "100%", padding: "0 15px" }}
        >
          <div style={{ width: "100%", height: "70vh" }}>
            <AgGridReact<CatalogBin>
              rowData={rows || []}
              columnDefs={columnDefs}
              defaultColDef={columnSimpleDefaults}
              rowHeight={34}
              headerHeight={34}
              animateRows
              rowSelection="single"
              getContextMenuItems={getContextMenuItems}
              onRowDoubleClicked={onRowDoubleClicked}
              loading={isLoading}
              onGridReady={(params) => setGridApi(params.api)}
              getRowId={(params) => String(params.data.BinId)}
              onCellClicked={onCellClicked}
            />
          </div>
          <FooterButton label="BIN" onClick={handleNewBin} />
        </Grid>
      </Grid>
      <BinModal
        bin={bin}
        showModal={showModal}
        onHide={({ entityId, shouldUpdate }) => {
          onModalHide({ entityId, shouldUpdate });
        }}
        refreshData={getCatalogBinsFromAPI}
      />
    </>
  );
};
